/**
 * The privacy policy.
 *
 * Written to describe what this server actually does, not what a template says a SaaS
 * usually does. Every claim here is checkable in the code: the Google scopes requested, the
 * columns that exist in the database, the fact that a GitHub token is encrypted with the
 * row it belongs to as additional authenticated data, and — the one that matters most for
 * Google's review — the Limited Use statement about what the Google data is and is not
 * used for.
 *
 * If the behaviour changes, this page changes with it. A policy that overstates what is
 * deleted or understates what is sent to a model vendor is worse than no policy.
 */
import { env } from '@/config/env'
import { Bullets, LegalDocument, Section } from './LegalDocument'

export function PrivacyPolicyPage() {
  return (
    <LegalDocument
      title="Privacy Policy"
      summary="What this service stores about you, why, and how to get rid of it."
    >
      <Section heading="What this service is">
        <p>
          RepoPilot is a tool for operating your own GitHub repositories through an AI
          assistant. You sign in, connect your own GitHub account, and the assistant reads and
          changes GitHub on your behalf. It is operated by {env.legal.operator}.
        </p>
      </Section>

      <Section heading="What we collect">
        <Bullets
          items={[
            <>
              <strong className="font-semibold text-ink">Your account.</strong> An email
              address and a display name. If you sign in with Google we receive these from
              Google, along with your Google account identifier and profile picture URL, using
              the <code className="mono">openid</code>, <code className="mono">email</code> and{' '}
              <code className="mono">profile</code> scopes. If you sign up with a password, we
              store a hash of it and never the password itself.
            </>,
            <>
              <strong className="font-semibold text-ink">Your GitHub connection.</strong> When
              you connect GitHub we store the access token GitHub issues, the account name it
              belongs to, and which permissions you granted. The token is encrypted before it
              is written down, and it is used only to make the GitHub requests you or the
              assistant ask for.
            </>,
            <>
              <strong className="font-semibold text-ink">Your conversations.</strong> The
              messages you send to the assistant and the answers it gives, so a conversation
              survives closing the tab.
            </>,
            <>
              <strong className="font-semibold text-ink">A record of every tool call.</strong>{' '}
              Which tool ran, against which repository, whether it succeeded, how long it
              took, and any error. This is what the Executions and Metrics pages show you. We
              store a one-way fingerprint of the arguments rather than the arguments
              themselves, so issue titles and bodies are not copied into the log.
            </>,
            <>
              <strong className="font-semibold text-ink">Your sessions.</strong> When you
              signed in, from what kind of browser, and when the session was last used — so
              you can see and end them on your profile page.
            </>,
          ]}
        />
        <p>
          We do not use advertising or analytics cookies, and there are no third-party
          trackers on these pages. The only cookie is the one that keeps you signed in.
        </p>
      </Section>

      <Section heading="What we do with it">
        <p>
          We use it to run the service you asked for and nothing else: to sign you in, to act
          on GitHub as you, to show you your own history, and to keep the service working. We
          do not sell your data, we do not share it for advertising, and we do not use it to
          build profiles of you.
        </p>
      </Section>

      <Section heading="Google user data — Limited Use">
        <p>
          Our use of information received from Google APIs follows the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary-bright underline-offset-2 hover:underline"
          >
            Google API Services User Data Policy
          </a>
          , including its Limited Use requirements.
        </p>
        <p>
          Concretely: we ask Google only who you are — your identifier, email address, name
          and profile picture — and we use it only to create and identify your account. We do
          not read your Gmail, Drive, Calendar or any other Google service, and we hold no
          Google access token after sign-in completes. Google data is never sold, never used
          for advertising, and never given to a third party except where you direct it or the
          law requires it.
        </p>
      </Section>

      <Section heading="GitHub data">
        <p>
          Your GitHub token is encrypted at rest with AES-256-GCM, tied to the database row it
          belongs to, so a copy lifted out of the database cannot be decrypted somewhere else.
          It is never shown in this interface, never sent to your browser, and never included
          in logs or error messages.
        </p>
        <p>
          Repository and issue content is read live from GitHub when you ask a question. Apart
          from the names of repositories involved in a tool call, we do not keep a copy.
        </p>
      </Section>

      <Section heading="The AI assistant">
        <p>
          When you use the chat, your message, the earlier messages in that conversation, and
          the results of any tool call are sent to the AI model provider this deployment is
          configured to use — Anthropic or OpenAI — so it can answer. Their handling of that
          text is governed by their own terms. Do not paste anything into the chat you would
          not want processed by a third-party model provider.
        </p>
        <p>
          Your GitHub token is never sent to the model provider. The assistant asks this server
          to act; the server holds the credential.
        </p>
      </Section>

      <Section heading="Who else sees it">
        <Bullets
          items={[
            'GitHub, when the assistant acts on your behalf — that is the point of the connection.',
            'Google, if you chose to sign in with Google.',
            'The AI model provider, for the chat messages described above.',
            'The hosting and database provider this deployment runs on.',
          ]}
        />
        <p>
          Other users of the same deployment never see your data. Every request is scoped to
          the signed-in account on the server.
        </p>
      </Section>

      <Section heading="How long we keep it">
        <p>
          Your account, conversations and execution history are kept until you delete them or
          ask for the account to be deleted. There is no automatic expiry, and we would rather
          say so than claim a retention period the software does not enforce.
        </p>
        <p>
          Disconnecting GitHub deletes the stored token immediately. Deleting a conversation
          deletes its messages. Signing out ends that session; you can also end every other
          session from your profile page.
        </p>
      </Section>

      <Section heading="Your choices">
        <Bullets
          items={[
            'Disconnect GitHub at any time, from Settings.',
            'Delete any conversation from the chat sidebar.',
            'End any session, or every other session, from your profile.',
            'Ask for your account and all of its data to be deleted — write to the address below and it will be removed.',
            'Ask for a copy of what is stored about you.',
          ]}
        />
      </Section>

      <Section heading="Security">
        <p>
          Credentials are encrypted at rest. The session cookie cannot be read by JavaScript,
          so a script injected into the page cannot steal it. Access is checked on the server
          for every request rather than in the browser. No system is perfectly secure, but
          these are the parts most often got wrong, and they are not left to chance here.
        </p>
      </Section>

      <Section heading="Children">
        <p>This service is not intended for anyone under 16.</p>
      </Section>

      <Section heading="Changes">
        <p>
          If this policy changes in a way that affects you, the date at the top changes and the
          new version appears here before it takes effect.
        </p>
      </Section>
    </LegalDocument>
  )
}
